import { useCallback, useEffect, useRef, useState } from 'react'

function getTimeFromSeconds(totalSeconds) {
  const t = Math.max(0, Math.ceil(Number(totalSeconds) || 0))
  return {
    totalSeconds: t,
    seconds: Math.floor(t % 60),
    minutes: Math.floor((t % 3600) / 60),
    hours: Math.floor((t % 86400) / 3600),
    days: Math.floor(t / 86400),
  }
}

/** Match react-timer-hook: positive ms-until-expiry → seconds, else 0. */
function secondsFromOffset(offset) {
  if (offset == null || offset === 0) return 0
  const ms = new Date(offset).getTime() - Date.now()
  if (ms > 0) return Math.round(ms / 1000)
  return 0
}

/**
 * Drop-in for `react-timer-hook`'s `useStopwatch`.
 * `reset()` defaults to auto-start (same as the library).
 */
export function useStopwatch({ autoStart = false, offsetTimestamp } = {}) {
  const initial = secondsFromOffset(offsetTimestamp)
  const [baseSeconds, setBaseSeconds] = useState(initial)
  const [startedAt, setStartedAt] = useState(() => new Date())
  const [elapsed, setElapsed] = useState(initial)
  const [isRunning, setIsRunning] = useState(!!autoStart)
  const elapsedRef = useRef(elapsed)
  elapsedRef.current = elapsed

  useEffect(() => {
    if (!isRunning) return undefined
    const id = setInterval(() => {
      const sinceStart = Math.round(
        (Date.now() - startedAt.getTime()) / 1000,
      )
      setElapsed(baseSeconds + sinceStart)
    }, 1000)
    return () => clearInterval(id)
  }, [isRunning, startedAt, baseSeconds])

  const start = useCallback(() => {
    const now = new Date()
    setStartedAt(now)
    setElapsed(baseSeconds)
    setIsRunning(true)
  }, [baseSeconds])

  const pause = useCallback(() => {
    setBaseSeconds(elapsedRef.current)
    setIsRunning(false)
  }, [])

  const reset = useCallback((offset = 0, newAutoStart = true) => {
    const nextBase = secondsFromOffset(offset)
    setStartedAt(new Date())
    setBaseSeconds(nextBase)
    setElapsed(nextBase)
    setIsRunning(newAutoStart)
  }, [])

  return {
    ...getTimeFromSeconds(elapsed),
    start,
    pause,
    reset,
    isRunning,
  }
}
